import React, { useState } from "react";
import { Grid, Row, Col } from "react-bootstrap";
import LoadingButton from "../../forms/loading_button/loading_button";
import { If } from "react-extras";
import BaseModalV2 from "../base_modal_v2";
import { HttpError } from "../../../lib/http";
import { ValidationError } from "../../../lib/errors";
import { getFormValues } from "redux-form";
import { useSelector } from "react-redux";
/*

props:

onSubmit: submit at the end of the wizzy wiz.

close modal
close: () => {}

pages: [] pageconfig
commonSettings: {
  things like showModal that will get spread on every page
}
page config is the config that will take place in a single modal, with a few extra goodies
pageconfig:
  Content: <div>content yay</div>

  onSubmit supports returning a promise, and will only go to the next page if the promise doesn't reject
  this is useful for for saving intermediate settings
  onSubmit: () => {  return   }

  if you want to control page progression. can leave blank for uncontrolled
  pageNumber: int
  onUpdatePage: function (pageNumber)

  disable next button
  disabled: boolean

  function version of disabled
  shouldDisable: () => boolean

  text on next button. defaults to "next" until "submit" on the last page
  submitText: string

  will not have a next button. only a big "close" button
  closeOnly: boolean

  no next button
  hideBack: Boolean

  hide this page
  hide: boolean

  default is "Submit" can change to "Done"
  submitText: string

  default is "Next"
  nextText: string

  default is "Cancel"
  cancelText: string

  default is "Back"
  backText: string
 */

export const MultiStepModal = props => {
  const {
    startingPage,
    pageNumber,
    onUpdatePage,
    pages,
    close,
    hideCloseButton,
    onSubmit,
    isLoading,
    commonSettings,
    modalProps,
    submitText = "Submit",
    nextText = "Next",
    cancelText = "Cancel",
    backText = "Back",
  } = props;

  let [currentPageNumber, updatePageInternal] = useState(startingPage ?? 0);
  const [submitting, updateSubmitting] = useState(false);
  if (pageNumber !== undefined) {
    currentPageNumber = pageNumber;
  }

  const formValues = useSelector(state => {
    const formName = pages?.[currentPageNumber]?.formName;
    return formName && getFormValues(formName)(state);
  });
  if (!Array.isArray(pages) || !pages.length) {
    return null;
  }

  const updatePage = val => {
    onUpdatePage !== undefined ? onUpdatePage(val) : updatePageInternal(val);
  };

  const currentPage = { ...pages[currentPageNumber], ...commonSettings };
  currentPage.isLoading = currentPage.isLoading || submitting; // prevents flickering when you click next

  const prevPageOrClose = () => {
    let prevPageNumber = currentPageNumber - 1;
    while (pages[prevPageNumber] && pages[prevPageNumber].hide) {
      prevPageNumber -= 1;
    }

    if (pages[prevPageNumber] === undefined) {
      close();
      return;
    }
    updatePage(prevPageNumber);
  };

  const callSubmit = async submitFunc => {
    updateSubmitting(true);
    return Promise.resolve(submitFunc(formValues))
      .then(nextPage)
      .catch(e => {
        // we want to throw the rest because real errors like syntax issues can silently fail
        if (!(e instanceof HttpError) && !(e instanceof ValidationError)) {
          throw e;
        }
      })
      .finally(() => updateSubmitting(false));
  };

  const nextPage = () => {
    let nextPageNumber = currentPageNumber + 1;
    while (pages[nextPageNumber] && pages[nextPageNumber].hide) {
      nextPageNumber += 1;
    }

    if (pages[nextPageNumber] === undefined) {
      close();
      return;
    }
    updatePage(nextPageNumber);
  };

  const nextPageOrSubmit = async () => {
    if (!currentPage.validator || currentPage.validator(formValues)) {
      if (currentPage.onSubmit) {
        await callSubmit(currentPage.onSubmit);
        return;
      }

      if (currentPageNumber === pages.length - 1 && onSubmit) {
        await callSubmit(onSubmit);
        return;
      }

      nextPage();
    }
  };
  return (
    <BaseModalV2
      modalProps={modalProps}
      close={close}
      hideCloseButton={hideCloseButton}
      Footer={
        <Grid fluid>
          <Row>
            <div className={"margin-top--large"}>
              <If condition={!currentPage.closeOnly}>
                <If condition={!currentPage.hideBack}>
                  <Col xs={6} md={4} mdOffset={2}>
                    <button className="btn btn-secondary width-100" onClick={prevPageOrClose}>
                      {currentPageNumber === 0 ? cancelText : backText}
                    </button>
                  </Col>
                </If>
                <Col
                  xs={6}
                  xsOffset={currentPage.hideBack ? 3 : 0}
                  md={currentPage.hideBack ? 6 : 4}
                  mdOffset={currentPage.hideBack ? 3 : 0}>
                  <LoadingButton
                    isLoading={isLoading || submitting}
                    className="btn btn-primary width-100"
                    disabled={
                      currentPage.disabled ||
                      (currentPage.shouldDisable && currentPage.shouldDisable(formValues))
                    }
                    onClick={nextPageOrSubmit}>
                    {currentPage.submitText ||
                      (currentPageNumber < pages.length - 1 ? nextText : submitText)}
                  </LoadingButton>
                </Col>
              </If>
              <If condition={!!currentPage.closeOnly}>
                <Col xs={6} xsOffset={3}>
                  <button className="btn btn-primary width-100" onClick={close}>
                    {currentPage.closeText || close}
                  </button>
                </Col>
              </If>
            </div>
          </Row>
        </Grid>
      }
      {...currentPage}
    />
  );
};
